import React, { useState, useEffect, useCallback } from 'react';
import { isAndroid, isIOS, isMobile } from 'react-device-detect';

import Analytics from 'utils/analytics';
import Icon from 'components/Icon';
import Tappable from 'components/common/Tappable';
import { useSendWithLoaderConfig } from '../../context/loaderContext';

const agencySlugLinkReplacements = {
    uber: {
        uri: "uber://",
        replacement: "https://m.uber.com/ul/",
    },
    lyft: {
        uri: "lyft://",
        replacement: "https://lyft.com/",
    },
    citibike: {
        uri: "citibike://"
    }
};

function ExternalAppButton(props) {
    const [externalLink, setExternalLink] = useState(false);
	const sendWithLoaderConfig = useSendWithLoaderConfig();
	// const shortCode = useShortCode();


    const canShowLink = useCallback(() => {
        let canShowLink = false;
        let searchQuery = window.location.search || document.location.search;
        let params = new URLSearchParams(searchQuery);
        canShowLink = params.has("externalLinks");
        if (process.env.NODE_ENV === 'development') canShowLink = true;
        return canShowLink;
    }, []);

    const getAppStoreLink = useCallback(() => {
        if (props.agency) {
            if (isIOS && props.agency.app.appStoreId) {
                return `https://apps.apple.com/us/app/${props.agency.app.appStoreId}`;
            } else if (isAndroid && props.agency.app.playStoreId) {
                return `https://play.google.com/store/apps/details?id=${props.agency.app.playStoreId}`;
            }
        }
        return false;
    }, [props.agency]);

    const getLink = useCallback((linkType) => {
        if (!isMobile) return null;

        if (linkType === "GET_VEHICLE") {
            let link = props.universalLink;
            if (!link && props.agency.app.iosUniversalLink) {
                link = props.agency.app.iosUniversalLink;
            } else if (!link && props.agency.app.androidUniversalLink) {
                link = props.agency.app.androidUniversalLink;
            } else {
                link = getAppStoreLink();
            }
            return link;
        }

        if (linkType === "GET_MOBILE_TICKET" || linkType === "GET_APP") {
            let searchQuery = window.location.search || document.location.search;
            let params = new URLSearchParams(searchQuery);
            let linkContext = params.get("externalLinks");

            if (isIOS) {
                if (linkContext === "universal") {
                    if (props.agency.app.iosUniversalLink) {
                        return props.agency.app.iosUniversalLink;
                    }
                } else {
                    if (props.agency.app.iosUri) {
                        return props.agency.app.iosUri;
                    } else if (props.agency.app.iosUniversalLink) {
                        return props.agency.app.iosUniversalLink;
                    }
                }
            } else {
                if (linkContext === "universal") {
                    if (props.agency.app.androidUniversalLink) {
                        return props.agency.app.androidUniversalLink;
                    }
                } else {
                    if (props.agency.app.androidUri) {
                        return props.agency.app.androidUri;
                    } else if (props.agency.app.androidUniversalLink) {
                        return props.agency.app.androidUniversalLink;
                    }
                }
            }
        }
    }, [props.universalLink, props.agency, getAppStoreLink]);

    useEffect(() => {
        if (canShowLink()) {
            let linkType = props.linkType;
            if (linkType) {
                let link = getLink(linkType);
                if (link) {
                    // Special: Swap Universal Link for URI instead
                    if (props.agency && props.agency.slug) {
                        let agencySlug = props.agency.slug;
                        if (agencySlugLinkReplacements[agencySlug]) {
                            if (props.linkType === "GET_VEHICLE") {
                                let uri = agencySlugLinkReplacements[agencySlug].uri;
                                link = link.replace(agencySlugLinkReplacements[agencySlug].replacement, uri);
                            }
                        }
                    }

                    // Append parameter for native code handling
                    var linkParams = link.includes("http") ? 'CM_UNIVERSAL_LINK' : 'CM_URI';

                    // Append store link for failures
                    let storeLink = getAppStoreLink();
                    if (storeLink) {
                        linkParams = `${linkParams}&APP_STORE_LINK=${storeLink}`;
                    }

                    // Finally append it to the link
                    link = link.includes("?") ? `${link}&${linkParams}` : `${link}?${linkParams}`;

                    setExternalLink(link);
                }
            }
        }
    }, [canShowLink, getLink, getAppStoreLink, props.linkType, props.agency]);

    const handleTapEvent = useCallback(async () => {
        const link = externalLink;

        Analytics.recordHit({
            t: 'event',
            ec: 'ExternalAppButton',
            ea: 'Tap Event',
            el: link
        });

        // Analytics.postToDataDrop({
        //     "agencyId": props.agency.id,
        //     "agencyName": props.agency.fullName,
        //     "agencyMode": props.agency.mode,
        //     "appName": props.agency.app.appName,
        //     "appLink": link,
        // }, "Mobile App Selection");

		await sendWithLoaderConfig({
            "agencyId": props.agency.id,
            "agencyName": props.agency.fullName,
            "agencyMode": props.agency.mode,
            "appName": props.agency.app.appName,
            "appLink": link,
        }, "Mobile App Selection");

        if (isMobile) {
            window.location.href = link;
        } else if (!isMobile && link && link.includes("http")) {
            window.open(link, "_new");
        } else {
            window.alert("Sorry this link doesn't work for your web browser.");
        }
    }, [externalLink, props.agency]);

    if (!externalLink) {
        return null;
    }

    const voText = props.agency ? `Tap to open ${props.agency.app.appName} app` : `Tap to open app`;

    return (
        <Tappable stopPropagation onTap={handleTapEvent} className={`ExternalAppButton ${props.className || ''}`}>
            <span className="voiceover-text">{voText}</span>
            {props.buttonText && (
                <>
                    <span aria-hidden="true">{props.buttonText}</span>
                    <Icon category="ui" name="appButtonArrow" />
                </>
            )}
        </Tappable>
    );
}

export default ExternalAppButton;
